import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject, takeUntil } from "rxjs";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { APP_INIT_STATES } from "../../store/status/status";
import { ButtonType } from "../../ui/button/button.type";
import { MessageType } from "../../ui/box/message-box/message.type";
import {
  JoliooPharmacy,
  JoliooReservation,
} from "../../io/jolioo/voucher.types";
import { VoucherRedemptionService } from "../../io/jolioo/voucher-redemption.service";
import { voucherErrorMessage } from "../../store/voucher/voucher-error";
import {
  GetActiveVoucherPharmacy,
  GetSelectedVoucherPharmacy,
  GetVoucher,
  GetVoucherCancelStatus,
  GetVoucherConfirmStatus,
  GetVoucherError,
  GetVoucherReservation,
} from "../../store/voucher/voucher.selector";

/** Schritt 3: Die Reservierung bestätigen oder wieder verwerfen. */
@Component({
  selector: "app-voucher-reservation",
  templateUrl: "./voucher-reservation.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VoucherReservationComponent implements OnInit, OnDestroy {
  public readonly STATES = APP_INIT_STATES;
  public readonly ButtonType = ButtonType;
  public readonly MessageType = MessageType;

  public reservation$: Observable<JoliooReservation | null>;
  public confirmStatus$: Observable<APP_INIT_STATES>;
  public cancelStatus$: Observable<APP_INIT_STATES>;
  public pharmacy$: Observable<JoliooPharmacy | null>;

  private _reservation: JoliooReservation | null = null;
  private _confirming = false;
  private _cancelling = false;
  private _pharmacy: string | null = null;
  private _code = "";
  private _error: any = null;
  private _destroy = new Subject<void>();

  get confirmError(): string {
    return voucherErrorMessage(
      this._error,
      "PAGES.VOUCHER.RESERVED.CONFIRM_ERROR",
      this._translate,
    );
  }

  get cancelError(): string {
    return voucherErrorMessage(
      this._error,
      "PAGES.VOUCHER.RESERVED.CANCEL_ERROR",
      this._translate,
    );
  }

  constructor(
    private _store: Store,
    private _router: Router,
    private _route: ActivatedRoute,
    private _redemption: VoucherRedemptionService,
    private _translate: TranslateService,
  ) {
    this.reservation$ = this._store.select(GetVoucherReservation);
    this.confirmStatus$ = this._store.select(GetVoucherConfirmStatus);
    this.cancelStatus$ = this._store.select(GetVoucherCancelStatus);
    this.pharmacy$ = this._store.select(GetActiveVoucherPharmacy);
  }

  /**
   * Guthaben vor dem Einlösen.
   *
   * ⚠ `value_current` ist während der Reservierung bereits um den reservierten
   * Betrag reduziert — der Ausgangswert ist erst die Summe aus beiden.
   */
  public balanceBefore(reservation: JoliooReservation): number {
    return (
      (reservation.voucher.value_current ?? 0) + (reservation.reserved_value ?? 0)
    );
  }

  ngOnInit(): void {
    // ⚠ Ohne offene Reservierung gibt es hier nichts zu bestätigen.
    this.reservation$.pipe(take(1)).subscribe((reservation) => {
      if (!reservation) {
        this._router.navigate(["."], { relativeTo: this._route.parent });
      }
    });

    this.reservation$
      .pipe(takeUntil(this._destroy))
      .subscribe((reservation) => (this._reservation = reservation));

    this._store
      .select(GetVoucher)
      .pipe(takeUntil(this._destroy))
      .subscribe((voucher) => (this._code = voucher?.code ?? ""));

    this._store
      .select(GetSelectedVoucherPharmacy)
      .pipe(takeUntil(this._destroy))
      .subscribe((pharmacy) => (this._pharmacy = pharmacy));

    this._store
      .select(GetVoucherError)
      .pipe(takeUntil(this._destroy))
      .subscribe((error) => (this._error = error));

    this.confirmStatus$.pipe(takeUntil(this._destroy)).subscribe((status) => {
      this._confirming = status === APP_INIT_STATES.LOADING;

      if (status === APP_INIT_STATES.SUCCESS) {
        this._redemption.complete();
        this._router.navigate(["erfolg"], { relativeTo: this._route.parent });
      }
    });

    this.cancelStatus$.pipe(takeUntil(this._destroy)).subscribe((status) => {
      this._cancelling = status === APP_INIT_STATES.LOADING;

      if (status === APP_INIT_STATES.SUCCESS) {
        // Vorgang beendet — ein neuer Versuch bekommt einen neuen Schlüssel.
        this._redemption.complete();
        this._router.navigate(["betrag"], { relativeTo: this._route.parent });
      }
    });
  }

  ngOnDestroy(): void {
    this.releaseReservation();

    this._destroy.next();
    this._destroy.complete();
  }

  /**
   * Gibt eine noch offene Reservierung beim Verlassen der Route frei.
   *
   * ⚠ Sonst bleibt der Betrag bei Jolioo blockiert, bis dessen Timeout ihn
   * zurückgibt — und das gilt für jeden Ausgang, nicht nur für "Betrag ändern".
   */
  private releaseReservation(): void {
    if (
      !this._reservation ||
      this._confirming ||
      this._cancelling ||
      !this._pharmacy
    ) {
      return;
    }

    this._redemption.cancel(this._code, this._pharmacy);
    this._redemption.complete();
  }

  /** Endgültig abbuchen. */
  public confirmReservation(): void {
    if (this._pharmacy) {
      this._redemption.confirm(this._code, this._pharmacy);
    }
  }

  /**
   * ⚠ Der Schritt wird erst nach der Antwort gewechselt. Vorher wäre die
   * Reservierung bei Jolioo noch offen, der Betrag weiter blockiert — und ein
   * erneutes Reservieren würde eine zweite Reservierung anlegen.
   */
  public cancelReservation(): void {
    if (this._pharmacy) {
      this._redemption.cancel(this._code, this._pharmacy);
    }
  }
}
